import { FC, useEffect, useCallback, useRef } from "react";

import {  Button, Spinner } from '@fluentui/react-components';
import { CheckmarkCircle24Filled, DismissCircle24Filled } from '@fluentui/react-icons';
import { OnboardingData } from "../../pages/OnboardingFlow/OnboardingFlow";
import { useStyles } from "./FinalOnboardingStep.style";
import { useAsyncFn } from "react-use";
import { OnboardingStepWrapper } from "./OnboardingStepWrapper";
import apiClient from "../../services/apiClient";
import useEventTracker from "../../hooks/useEventTracker";
import { ElementButton, PageName } from "../../tracking/tracking.types";

type FinalOnboardingStepProps = {
  onboardingData: OnboardingData
  onNext: () => void;
  onBack: () => void;
  currentStep: number;
  totalSteps: number;
}

export const FinalOnboardingStep: FC<FinalOnboardingStepProps> = ({ onboardingData, onNext, onBack, currentStep, totalSteps }) => {
  const styles = useStyles();

  const eventTracker = useEventTracker();
  const pageLoadStartTime = useRef<number>(performance.now());
  const hasTrackedPageLoad = useRef<boolean>(false);
  const hasTrackedCompletion = useRef<boolean>(false);

  useEffect(() => {
    if (!hasTrackedPageLoad.current && eventTracker.isUsageTrackingInitialized) {
      const loadTimeMs = Math.round(performance.now() - pageLoadStartTime.current);
      eventTracker.eventTrackOnPageLoaded({
        pageName: PageName.ONBOARDING_SETUP,
        loadTimeMs,
      });
      hasTrackedPageLoad.current = true;
    }
  }, [eventTracker.isUsageTrackingInitialized]);

  const [{ value: dataProviderConnected, loading: dataProviderLoading, error: dataProviderError }, setUpDataProviderConnection] = useAsyncFn(async () => {
    if(!onboardingData.connectDataProvider?.providerKey) {
      throw new Error("No provider key");
    }
    const lsegResponse = await apiClient.post<{ success?: boolean }>('/lseg/config', {
      apiKey: onboardingData.connectDataProvider.providerKey,
    });
    if(lsegResponse.success)
      return true;
    return false;
  }, [onboardingData]);

  // Feel free to uncomment when you need to add more tasks
  // const [{ value: somethingElseSetUp, loading: somethingElseLoading, error: somethingElseError }, setUpSomethingElse] = useAsyncFn(async () => {
  //   try {
  //     await new Promise((resolve) => setTimeout(resolve, 2000));
  //     return true;
  //   }
  //   catch {
  //     return false;
  //   }
  // }, [onboardingData]);

  const tryAgain = useCallback(() => {
    setUpDataProviderConnection();
    // setUpSomethingElse();
  }, [setUpDataProviderConnection, 
    /*setUpSomethingElse*/]);

  useEffect(() => {
    tryAgain();
  }, [tryAgain]);

  const isLoading = dataProviderLoading /*|| somethingElseLoading*/;
  const hasError = Boolean(dataProviderError /*|| somethingElseError*/);
  const isSuccess = Boolean(dataProviderConnected /*&& somethingElseSetUp*/ );

  useEffect(() => {
    if (!hasTrackedCompletion.current && !isLoading && eventTracker.isUsageTrackingInitialized) {
      eventTracker.eventTrackOnOnboardingCompleted({
        success: isSuccess,
        errorMessage: hasError ? (dataProviderError?.message || 'Setup failed') : undefined,
      });
      hasTrackedCompletion.current = true;
    }
  }, [isLoading, isSuccess, hasError, dataProviderError, eventTracker.isUsageTrackingInitialized]);

  const handleGoToPolicies = () => {
    eventTracker.eventTrackOnElementClicked({
      pageName: PageName.ONBOARDING_SETUP,
      elementName: ElementButton.GO_TO_POLICIES,
      elementType: 'button',
    });
    onNext();
  };

  const handleTryAgain = () => {
    eventTracker.eventTrackOnElementClicked({
      pageName: PageName.ONBOARDING_SETUP,
      elementName: ElementButton.TRY_AGAIN,
      elementType: 'button',
    });
    hasTrackedCompletion.current = false;
    tryAgain();
  };

  const handleBack = () => {
    eventTracker.eventTrackOnElementClicked({
      pageName: PageName.ONBOARDING_SETUP,
      elementName: ElementButton.BACK,
      elementType: 'button',
    });
    onBack();
  };

  return (
    <OnboardingStepWrapper 
      title="Setting things up"
      currentStep={currentStep}
      totalSteps={totalSteps}
    >
      <div className={styles.tasks} data-testid="onboarding-final-tasks">
        <OnboardingTaskItem 
          loading={dataProviderLoading} 
          success={dataProviderConnected ?? false} 
          label="Setting up connection to the data provider" 
        />
        {/* <OnboardingTaskItem 
          loading={somethingElseLoading} 
          success={somethingElseSetUp ?? false} 
          label="Setting up something else" 
        /> */}
      </div>

      <div className={styles.buttonContainer} data-testid="onboarding-final-button-container">
        <Button onClick={handleBack} data-testid="onboarding-setup-back">Back</Button>
        <Button appearance="primary" onClick={handleGoToPolicies} disabled={!isSuccess} data-testid="onboarding-go-to-policies">Go to Policies</Button>
        <Button appearance="primary" onClick={handleTryAgain} disabled={!(!isLoading && hasError)} data-testid="onboarding-try-again">Try Again</Button>
      </div>
    </OnboardingStepWrapper>
  );
}

type OnboardingTaskItemProps = {
  loading: boolean;
  success: boolean;
  label: string;
}

const OnboardingTaskItem: FC<OnboardingTaskItemProps> = ({ loading, success, label }) => {
  const styles = useStyles();

  return (
    <div className={styles.taskContainer} data-testid="onboarding-final-task-item">
      {loading && <Spinner size="medium" data-testid="onboarding-final-task-spinner" />}
      {!loading && success === true && <CheckmarkCircle24Filled style={{ color: 'green' }} />}
      {!loading && success === false && <DismissCircle24Filled style={{ color: 'red' }} />}
      <span data-testid="onboarding-final-task-label">{label}</span>
    </div>
  );
}